import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getChannels, getCategories, addFavorite, removeFavorite, getFavorites } from '../services/api';
import { toast } from 'sonner';
import { Play, Heart, Search, Filter, Radio, Tv2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

export default function DashboardPage() {
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchQuery = searchParams.get('search') || '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [channelsData, categoriesData, favoritesData] = await Promise.all([
        getChannels({ 
          category: selectedCategory, 
          search: searchQuery,
          limit: 100 
        }),
        getCategories(),
        getFavorites()
      ]);
      
      setChannels(channelsData);
      setCategories(categoriesData);
      setFavorites(favoritesData.map(f => f.id));
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('No active subscription. Contact admin for access.');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFavoriteToggle = async (channelId, e) => {
    e.stopPropagation();
    const isFavorite = favorites.includes(channelId);
    
    try {
      if (isFavorite) {
        await removeFavorite(channelId);
        setFavorites(prev => prev.filter(id => id !== channelId));
        toast.success('Removed from favorites');
      } else {
        await addFavorite(channelId);
        setFavorites(prev => [...prev, channelId]);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleChannelClick = (channelId) => {
    navigate(`/watch/${channelId}`);
  };

  // Group channels by category for display
  const groupedChannels = categories.reduce((acc, cat) => {
    const categoryChannels = channels.filter(ch => ch.group === cat.name);
    if (categoryChannels.length > 0) {
      acc[cat.name] = categoryChannels;
    }
    return acc;
  }, {});

  // Also include channels without matching category
  const uncategorized = channels.filter(ch => !categories.find(cat => cat.name === ch.group));
  if (uncategorized.length > 0) {
    groupedChannels['Other'] = uncategorized;
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Hero Section */}
      <div className="relative rounded-2xl overflow-hidden h-64 lg:h-80">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1705593973313-75de7bf95b56?crop=entropy&cs=srgb&fm=jpg&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="live-badge px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
              <Radio className="w-3 h-3" />
              LIVE
            </span>
            <span className="px-3 py-1 bg-white/20 text-white text-xs rounded-full">Sports</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold mb-2">Live Sports</h2>
          <p className="text-white/70 max-w-lg mb-4">
            Watch live sports events from around the world in HD quality
          </p>
          <Button 
            className="w-fit bg-[rgb(124,58,237)] hover:bg-[rgb(124,58,237)]/90 rounded-full px-8 btn-press"
            onClick={() => setSelectedCategory('Sports')}
            data-testid="watch-sports-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Watch Now
          </Button>
        </div>
      </div>

      {/* Category Pills */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold">Categories</h3>
          <span className="text-white/40 text-sm">{channels.length} channels</span>
        </div>
        
        <ScrollArea className="w-full whitespace-nowrap">
          <div className="flex gap-3 pb-4">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`category-pill px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                !selectedCategory 
                  ? 'bg-[rgb(124,58,237)] text-white' 
                  : 'bg-[rgb(31,31,34)] text-white/60 hover:text-white'
              }`}
              data-testid="category-all"
            >
              All Channels
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.name)}
                className={`category-pill px-5 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === cat.name 
                    ? 'bg-[rgb(124,58,237)] text-white' 
                    : 'bg-[rgb(31,31,34)] text-white/60 hover:text-white'
                }`}
                data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat.name}
                <span className="text-xs opacity-60">({cat.channel_count})</span>
              </button>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="aspect-video rounded-xl skeleton" />
          ))}
        </div>
      )}

      {/* Channel Grid - by Category or Flat List */}
      {!loading && selectedCategory ? (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold">{selectedCategory}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {channels.map((channel) => (
              <ChannelCard 
                key={channel.id}
                channel={channel}
                isFavorite={favorites.includes(channel.id)}
                onFavoriteToggle={handleFavoriteToggle}
                onClick={() => handleChannelClick(channel.id)}
              />
            ))}
          </div>
        </div>
      ) : !loading && (
        Object.entries(groupedChannels).map(([categoryName, categoryChannels]) => (
          <div key={categoryName} className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">{categoryName}</h3>
              <button 
                onClick={() => setSelectedCategory(categoryName)}
                className="text-[rgb(124,58,237)] text-sm hover:underline"
              >
                View All
              </button>
            </div>
            <ScrollArea className="w-full">
              <div className="flex gap-4 pb-4">
                {categoryChannels.slice(0, 10).map((channel) => (
                  <div key={channel.id} className="flex-shrink-0 w-56">
                    <ChannelCard 
                      channel={channel}
                      isFavorite={favorites.includes(channel.id)}
                      onFavoriteToggle={handleFavoriteToggle}
                      onClick={() => handleChannelClick(channel.id)}
                    />
                  </div>
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        ))
      )}

      {/* Empty State */}
      {!loading && channels.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-[rgb(31,31,34)] flex items-center justify-center mb-6">
            <Tv2 className="w-10 h-10 text-white/30" />
          </div>
          <h3 className="text-xl font-semibold mb-2">No Channels Found</h3>
          <p className="text-white/50 max-w-md mb-6">
            {searchQuery 
              ? `No channels match "${searchQuery}". Try a different search.`
              : 'No channels available. Ask your admin to add playlists.'
            }
          </p>
        </div>
      )}
    </div>
  );
}

// Channel Card Component
function ChannelCard({ channel, isFavorite, onFavoriteToggle, onClick }) {
  const [imageError, setImageError] = useState(false);

  return (
    <div 
      className="channel-card group relative rounded-xl overflow-hidden bg-[rgb(15,15,16)] border border-white/5 cursor-pointer"
      onClick={onClick}
      data-testid={`channel-card-${channel.id}`}
    >
      <div className="aspect-video relative overflow-hidden">
        {channel.logo && !imageError ? (
          <img 
            src={channel.logo} 
            alt={channel.name}
            onError={() => setImageError(true)}
            className="channel-image w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[rgb(124,58,237)]/20 to-[rgb(16,185,129)]/20 flex items-center justify-center">
            <Tv2 className="w-12 h-12 text-white/20" />
          </div>
        )}
        
        {/* Gradient Overlay */}
        <div className="gradient-overlay absolute inset-0" />
        
        {/* Play Button */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 rounded-full bg-[rgb(124,58,237)] flex items-center justify-center glow-primary">
            <Play className="w-6 h-6 text-white ml-1" />
          </div>
        </div>

        {/* Favorite Button */}
        <button
          onClick={(e) => onFavoriteToggle(channel.id, e)}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
            isFavorite 
              ? 'bg-red-500 text-white' 
              : 'bg-black/50 text-white/60 opacity-0 group-hover:opacity-100 hover:text-white'
          }`}
          data-testid={`favorite-btn-${channel.id}`}
        >
          <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
        </button>
      </div>
      
      <div className="p-3">
        <h4 className="font-medium truncate">{channel.name}</h4>
        <p className="text-xs text-white/40 truncate">{channel.group}</p>
      </div>
    </div>
  );
}
